import React, { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import {
  User,
  KeyRound,
  Webhook,
  Users,
  MessageSquare,
  Cpu,
  Handshake,
  Landmark,
  Briefcase,
  Workflow,
  Tags,
  Timer,
  Gauge,
  Bell,
  Radio,
  Repeat,
  Receipt,
  Settings as SettingsIcon,
  ChevronDown,
  ChevronRight,
  Plug,
} from "lucide-react";

/**
 * Settings IA shell — left-rail sidebar + nested content area.
 * SOUND-319.
 *
 * Each section is its own route under /settings. Telegram is a collapsible
 * group with four child routes. Processors lives as a standalone section
 * within Settings.
 *
 * SOUND-681: two entries here are NOT /settings routes. Rerunner and Billing
 * were demoted out of the primary nav into this rail, but their routes stayed
 * where they are (/rerunner, /billing/*) — Billing in particular keeps its own
 * provider rail (BillingLayout), which this rail links out to. Consequence
 * worth knowing: clicking either leaves the Settings shell, so their NavLink
 * active state never renders. That is the accepted trade for not reparenting
 * working routes. Audit Log went the other way — promoted to the CRM section
 * of the primary nav and renamed "Change Log".
 *
 * Renders different sidebar items per user type (internal sees everything,
 * partners see a smaller subset, clients don't get this hub at all).
 */
export default function SettingsLayout() {
  const { user } = useAuth();
  const internal = isInternal(user);
  const isAdmin = internal || user?.role === "admin" || user?.role === "super";
  const location = useLocation();
  const telegramOpen = location.pathname.startsWith("/settings/telegram");
  const processorsOpen = location.pathname.startsWith("/settings/processors");
  const [openGroups, setOpenGroups] = useState({
    telegram: telegramOpen,
    processors: processorsOpen,
  });

  const toggleGroup = (key) =>
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  // Order (CPO 2026-07-20): Profile → Team → Functions → Workflows → Processors
  // → Telegram → API Keys → Webhooks. Audit Log held the slot after Processors
  // until SOUND-681 promoted it to the primary nav; Rerunner and Billing took
  // that slot on the way down.
  // SOUND-672 inserts Agents and Banks either side of Processors — the three
  // are one counterparty chain and read as a group, so Processors keeps its
  // documented slot and the two new registries bracket it.
  const sections = [
    {
      label: "Profile",
      to: "/settings/profile",
      icon: <User className="h-4 w-4" />,
      visible: true,
    },
    {
      label: "Notifications",
      to: "/settings/notifications",
      icon: <Bell className="h-4 w-4" />,
      visible: internal,
    },
    {
      label: "Notification Config",
      to: "/settings/notification-config",
      icon: <Radio className="h-4 w-4" />,
      visible: internal,
    },
    {
      label: "Team",
      to: "/settings/team",
      icon: <Users className="h-4 w-4" />,
      visible: isAdmin,
    },
    {
      label: "Job Functions",
      to: "/settings/functions",
      icon: <Briefcase className="h-4 w-4" />,
      visible: internal,
    },
    // SOUND-544 — the request-type vocabulary, ahead of the workflows built on it.
    {
      label: "Request Types",
      to: "/settings/request-types",
      icon: <Tags className="h-4 w-4" />,
      visible: internal,
    },
    {
      label: "Workflows",
      to: "/settings/workflows",
      icon: <Workflow className="h-4 w-4" />,
      visible: internal,
    },
    {
      label: "SLA Policies",
      to: "/settings/sla-policies",
      icon: <Timer className="h-4 w-4" />,
      visible: internal,
    },
    {
      label: "Workload",
      to: "/settings/workload",
      icon: <Gauge className="h-4 w-4" />,
      visible: internal,
    },
    // The counterparty chain, kept adjacent and in order (SOUND-672):
    // AGENT -> PROCESSOR -> SPONSOR BANK. "Agents", never "ISOs" — an agent is
    // an ISO for a processor and a processor is itself an ISO for its banks.
    {
      label: "Agents",
      to: "/settings/agents",
      icon: <Handshake className="h-4 w-4" />,
      visible: internal,
    },
    {
      label: "Processors",
      to: "/settings/processors",
      icon: <Cpu className="h-4 w-4" />,
      visible: internal,
    },
    {
      label: "Banks",
      to: "/settings/banks",
      icon: <Landmark className="h-4 w-4" />,
      visible: internal,
    },
    // SOUND-681 — demoted from the primary nav. Both link OUT of /settings.
    // Rerunner's route admits clients too, but clients keep their own
    // top-level Rerunner item and never see this rail's internal sections, so
    // gating on `internal` here matches where it came from.
    {
      label: "Rerunner",
      to: "/rerunner",
      icon: <Repeat className="h-4 w-4" />,
      visible: internal,
    },
    {
      // SOUND-709 (CPO 2026-08-26). Pipeline Config was the last non-credential
      // tenant of /integrations. SOUND-681 moved Rerunner and Billing out of
      // that section for exactly this reason; this follows them, which leaves
      // /credentials holding only credentials.
      //
      // Expected to move again: managed-services onboarding, or Track B
      // self-serve connect (SOUND-663), will want it somewhere a client can
      // reach. Internal-only until then.
      label: "Pipeline Config",
      to: "/settings/pipeline-config",
      icon: <Plug className="h-4 w-4" />,
      visible: internal,
    },
    {
      // Billing (SOUND-447) — internal-only AR view. Lands on Disputifier, the
      // first provider; BillingLayout's own rail carries the rest as they land.
      label: "Billing",
      to: "/billing/disputifier",
      icon: <Receipt className="h-4 w-4" />,
      visible: internal,
    },
    {
      // SOUND-247: was a dropdown with Subscribers + Notifications. The
      // Notifications tab carried a free-text BROADCAST TO ALL SUBSCRIBERS with
      // no confirmation step — one click, every channel. Removed rather than
      // gated: an approval flow is a real feature, and until we need CRM ->
      // Telegram messaging we should not carry the blast radius.
      // What is left is the read-only subscriber list, so no dropdown.
      label: "Telegram",
      to: "/settings/telegram/subscribers",
      icon: <MessageSquare className="h-4 w-4" />,
      visible: internal,
    },
    {
      label: "API Keys",
      to: "/settings/api-keys",
      icon: <KeyRound className="h-4 w-4" />,
      visible: true,
    },
    {
      label: "Webhooks",
      to: "/settings/webhooks",
      icon: <Webhook className="h-4 w-4" />,
      visible: true,
    },
  ].filter((s) => s.visible);

  return (
    <div className="flex flex-col lg:flex-row min-h-[calc(100vh-3.5rem)]">
      {/* Sidebar */}
      <aside className="lg:w-64 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-border">
        <div className="sticky top-0 px-3 py-5">
          <div className="flex items-center gap-2 px-2 mb-4">
            <SettingsIcon className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-base font-semibold text-foreground">Settings</h2>
          </div>

          <nav className="space-y-0.5">
            {sections.map((section) => {
              if (section.children) {
                const isOpen = openGroups[section.key];
                return (
                  <div key={section.label}>
                    <button
                      onClick={() => toggleGroup(section.key)}
                      className="w-full flex items-center justify-between px-2 py-2 text-sm rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        {section.icon}
                        {section.label}
                      </span>
                      {isOpen ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="ml-6 mt-0.5 space-y-0.5 border-l border-border pl-2">
                        {section.children.map((child) => (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            className={({ isActive }) =>
                              `block px-2 py-2 text-sm rounded-md transition-colors ${
                                isActive
                                  ? "bg-brand/10 text-foreground font-medium"
                                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
                              }`
                            }
                          >
                            {child.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={section.to}
                  to={section.to}
                  end={section.to === "/settings/processors"}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors ${
                      isActive
                        ? "bg-brand/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`
                  }
                >
                  {section.icon}
                  {section.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
